import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, ShoppingCart, User, LogOut, LayoutDashboard } from "lucide-react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useAuthStore } from "@/store/authStore";
import { useLogout } from "@/features/auth/hooks";
import { useCart } from "@/features/cart/hooks";
import { usePublicSettings } from "@/features/catalog/hooks";

const NAV_ITEMS = [
  { to: "/", label: "Anasayfa", end: true },
  { to: "/egitimler", label: "Eğitimler" },
  { to: "/paketler", label: "Paketler" },
  { to: "/kitaplar", label: "Kitaplarımız" },
  { to: "/ucretsiz-quizler", label: "Denemeler" },
  { to: "/hakkimizda", label: "Hakkımızda" },
  { to: "/blog", label: "Blog" },
  { to: "/iletisim", label: "İletişim" },
];


export default function Header() {
  const [open, setOpen] = useState(false);
  const user = useAuthStore((s) => s.user);
  const logout = useLogout();
  const navigate = useNavigate();
  const { data: cart } = useCart();
  const { data: settings } = usePublicSettings();
  const cartCount = cart?.items.reduce((sum, i) => sum + i.quantity, 0) ?? 0;
  const logoUrl = settings?.site_logo_header_url;
  const logoAlt = settings?.site_logo_alt || "Mersa Akademi";

  const handleLogout = () => {
    logout.mutate(undefined, {
      onSuccess: () => navigate("/"),
    });
  };

  const panelLink = user?.role === "ADMIN" ? "/admin" : "/panel";

  return (
    <header className="sticky top-0 z-30 w-full border-b border-border/60 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80">
      <div className="container-wide flex h-16 items-center justify-between gap-4">
        <Link to="/" className="flex shrink-0 items-center gap-2.5" onClick={() => setOpen(false)}>
          {/* Container sabit: gradient + "M" arka planda; logo varsa üstüne resim biner.
              Bu sayede settings refetch'lerinde img conditional toggle olmaz, flicker yok. */}
          <div className="relative flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-brand-gradient text-base font-bold text-white shadow-soft">
            <span aria-hidden>M</span>
            {logoUrl && (
              <img
                src={logoUrl}
                alt={logoAlt}
                width={40}
                height={40}
                decoding="async"
                className="absolute inset-0 h-full w-full object-cover"
              />
            )}
          </div>
          <div className="leading-tight whitespace-nowrap">
            <div className="font-bold text-foreground">Mersa Akademi</div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">JSPS Hazırlık</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                cn(
                  "whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium transition-colors",
                  isActive
                    ? "text-primary bg-secondary"
                    : "text-muted-foreground hover:text-foreground hover:bg-muted"
                )
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" asChild className="relative">
            <Link to="/sepet" aria-label="Sepet">
              <ShoppingCart className="h-5 w-5" />
              {cartCount > 0 && (
                <span className="absolute -right-1 -top-1 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-brand-gradient px-1 text-[10px] font-bold text-white shadow-soft">
                  {cartCount}
                </span>
              )}
            </Link>
          </Button>
          {user ? (
            <>
              <Button variant="outline" size="sm" asChild className="hidden sm:inline-flex">
                <Link to={panelLink}>
                  <LayoutDashboard className="h-4 w-4" />
                  {user.role === "ADMIN" ? "Admin" : "Panel"}
                </Link>
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                disabled={logout.isPending}
                className="hidden sm:inline-flex"
              >
                <LogOut className="h-4 w-4" />
                Çıkış
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" size="sm" asChild className="hidden sm:inline-flex">
                <Link to="/giris">
                  <User className="h-4 w-4" />
                  Giriş
                </Link>
              </Button>
              <Button variant="brand" size="sm" asChild className="hidden sm:inline-flex">
                <Link to="/kayit">Kayıt Ol</Link>
              </Button>
            </>
          )}

          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden"
            onClick={() => setOpen((v) => !v)}
            aria-label="Menüyü aç/kapat"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </Button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-border bg-background">
          <nav className="container-wide flex flex-col py-3">
            {NAV_ITEMS.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  cn(
                    "rounded-md px-3 py-2.5 text-sm font-medium transition-colors",
                    isActive
                      ? "text-primary bg-secondary"
                      : "text-foreground hover:bg-muted"
                  )
                }
              >
                {item.label}
              </NavLink>
            ))}
            <div className="mt-2 flex gap-2 px-1 pt-2 border-t border-border">
              {user ? (
                <>
                  <Button variant="outline" size="sm" asChild className="flex-1">
                    <Link to={panelLink} onClick={() => setOpen(false)}>Panelim</Link>
                  </Button>
                  <Button variant="ghost" size="sm" onClick={handleLogout} className="flex-1">
                    Çıkış
                  </Button>
                </>
              ) : (
                <>
                  <Button variant="outline" size="sm" asChild className="flex-1">
                    <Link to="/giris" onClick={() => setOpen(false)}>Giriş</Link>
                  </Button>
                  <Button variant="brand" size="sm" asChild className="flex-1">
                    <Link to="/kayit" onClick={() => setOpen(false)}>Kayıt Ol</Link>
                  </Button>
                </>
              )}
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
